/**
 * Badge, status dot and the environment badge.
 *
 * Every status indicator here carries colour **plus text** — never colour
 * alone. Roughly 1 in 12 men have some colour-vision deficiency, and a
 * green dot that means "connected" and a red dot that means "the feed is
 * dead" must be distinguishable to all of them.
 */

import type { ReactNode } from 'react';

import type { Environment } from '@/api/types';
import { cn } from '@/lib/cn';
import { TONE_BG, TONE_BORDER, TONE_TEXT, labelForEnvironment, toneForEnvironment, type Tone } from '@/lib/colors';
import { ENVIRONMENT_LABELS } from '@/lib/terms';

import type { BadgeSize, IconComponent } from './types';

/**
 * @param tone      Semantic colour, from `lib/colors`. Never a raw class.
 * @param outlined  Draw the 1px border. Off inside a table cell.
 * @param children  The text. A badge is never colour alone.
 */
export interface BadgeProps {
  children: ReactNode;
  tone?: Tone;
  size?: BadgeSize;
  /** Draw the 1px border. Off for badges sitting inside a table cell. */
  outlined?: boolean;
  icon?: IconComponent;
  className?: string;
  title?: string;
  uppercase?: boolean;
}

export function Badge({
  children,
  tone = 'neutral',
  size = 'sm',
  outlined = true,
  icon: Icon,
  className,
  title,
  uppercase = false,
}: BadgeProps) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded font-medium',
        size === 'xs' ? 'h-4 px-1 text-[10px]' : 'h-5 px-1.5 text-[11px]',
        TONE_BG[tone],
        TONE_TEXT[tone],
        outlined && cn('border', TONE_BORDER[tone]),
        uppercase && 'tracking-wide uppercase',
        className,
      )}
    >
      {Icon && <Icon size={size === 'xs' ? 9 : 11} aria-hidden />}
      {children}
    </span>
  );
}

/**
 * Status dot with a mandatory text label.
 *
 * The label is a required prop, not optional. A bare coloured dot is a
 * status indicator that a colourblind user cannot read.
 */
export function StatusDot({
  tone,
  label,
  pulse = false,
  className,
}: {
  tone: Tone;
  label: string;
  /** Slow pulse for "live and receiving". Respects prefers-reduced-motion. */
  pulse?: boolean;
  className?: string;
}) {
  const dotColor: Record<Tone, string> = {
    up: 'bg-up',
    down: 'bg-down',
    accent: 'bg-accent',
    warn: 'bg-warn',
    danger: 'bg-danger',
    info: 'bg-info',
    neutral: 'bg-mid',
    muted: 'bg-lo',
  };
  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <span
        aria-hidden
        className={cn('size-1.5 shrink-0 rounded-full', dotColor[tone], pulse && 'pulse-live')}
      />
      <span className={cn('text-[11px] font-medium', TONE_TEXT[tone])}>{label}</span>
    </span>
  );
}

/**
 * The environment badge.
 *
 * Permanently visible, never collapsed into an icon, and unmistakable when
 * LIVE. It is the only thing on screen distinguishing a process that can
 * move real money from one that cannot.
 */
export function EnvironmentBadge({
  environment,
  venue,
  className,
  size = 'sm',
}: {
  environment: Environment;
  venue?: string;
  className?: string;
  size?: BadgeSize;
}) {
  const tone = toneForEnvironment(environment);
  const isLive = environment === 'live';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded border font-bold tracking-widest uppercase',
        size === 'xs' ? 'h-4 px-1.5 text-[10px]' : 'h-6 px-2 text-[11px]',
        isLive
          ? 'border-danger bg-danger text-danger-fg'
          : cn(TONE_BG[tone], TONE_TEXT[tone], TONE_BORDER[tone]),
        className,
      )}
      title={
        isLive
          ? `${ENVIRONMENT_LABELS.live} — orders placed here move real money${venue ? ` on ${venue}` : ''}`
          : `${ENVIRONMENT_LABELS[environment]} — no real money is at risk`
      }
    >
      {labelForEnvironment(environment)}
      {venue && (
        <span className={cn('font-normal opacity-75', isLive ? '' : 'text-mid')}>{venue}</span>
      )}
    </span>
  );
}

/**
 * The full-width LIVE banner.
 *
 * Rendered above everything when the backend reports `is_live`. Deliberately
 * loud and deliberately not dismissible: a banner you can close is a banner
 * that will be closed on the day it matters.
 */
export function LiveBanner({ venue }: { venue?: string }) {
  return (
    <div
      role="alert"
      className="flex h-6 shrink-0 items-center justify-center gap-2 bg-danger px-3 text-[11px] font-bold tracking-widest text-danger-fg uppercase"
    >
      <span className="size-1.5 rounded-full bg-danger-fg pulse-live" aria-hidden />
      Live trading — real money{venue ? ` · ${venue}` : ''}
      <span className="size-1.5 rounded-full bg-danger-fg pulse-live" aria-hidden />
    </div>
  );
}

/**
 * "Not protected" pill.
 *
 * The single highest-severity thing this UI can render: an open position
 * whose resting stops do not cover its full size. No amber middle ground —
 * partially protected is unprotected.
 */
export function ProtectionBadge({
  isFullyProtected,
  size = 'sm',
}: {
  isFullyProtected: boolean;
  size?: BadgeSize;
}) {
  return (
    <Badge
      tone={isFullyProtected ? 'up' : 'danger'}
      size={size}
      uppercase
      title={
        isFullyProtected
          ? 'Protective stops resting on the exchange cover the whole position'
          : 'Resting stops do NOT cover the whole position — this is an emergency'
      }
    >
      {isFullyProtected ? 'stop on venue' : 'unprotected'}
    </Badge>
  );
}
